// mission-actor-binding.ts
//
// Bug J (P15): `approveGate` (gate-registry-transitions.ts) enforces
// `actorId !== boundActorId => GateRejectedError`, which is the rule that
// stops a worker (or anyone else) self-authorising a gate. But the bound
// identity was never PERSISTED anywhere: `InitMissionInput.actorOsAccount`
// was accepted at `autopilot init` and then dropped on the floor — no file,
// no journal record. So the only caller that ever reached approveGate (the
// G3 release path) had to be handed a boundActorId from somewhere else, and
// a directive-driven approval had no bound identity to compare against at
// all.
//
// Without this file, the approve consumer would have exactly two options,
// both wrong: trust the directive's own `actor_id` (which lets any actor
// self-authorise — the precise thing the binding exists to prevent), or
// skip the check (same result). So the binding is written at init and read
// back here, and a MISSING binding fails closed.
//
// Spec §"Gate, danh tính và tài nguyên": "Định danh CLI local gắn OS
// account/uid tại setup và recorded authority của chủ mission, chỉ chấp
// nhận từ môi trường máy đã cấp quyền." Note the spec's own scope limit —
// a local file cannot defend against a hostile machine owner; it defends
// sessions/workers and operator error.
import { readFile } from "node:fs/promises";
import { join } from "node:path";
import { writeFileDurable } from "./durable-file-writer.js";

const BINDING_FILE_NAME = "mission-actor-binding.json";

export interface MissionActorBinding {
  /** OS account bound at `autopilot init` — the human owner of this mission. */
  os_account: string;
  /**
   * The SAME person's Mattermost user id, when one was configured at init
   * (P19b). Two directive producers write `actor_id` in different
   * namespaces — the CLI writes `process.env.USER`, the Mattermost sync
   * writes the 26-char Mattermost user id — so a binding holding only
   * `os_account` could never match a chat-filed approval, making G3
   * approval from chat structurally impossible regardless of transport
   * wiring. Optional: missions initialised before P19b have none, and MUST
   * keep failing closed on the chat path rather than falling back to
   * trusting the directive's own actor_id.
   */
  mattermost_user_id?: string;
  bound_at: string;
}

function bindingPath(controlRoot: string): string {
  return join(controlRoot, BINDING_FILE_NAME);
}

export async function writeMissionActorBinding(
  controlRoot: string,
  binding: MissionActorBinding,
): Promise<void> {
  const payload = { schema_version: 1, ...binding };
  await writeFileDurable(
    bindingPath(controlRoot),
    Buffer.from(`${JSON.stringify(payload, null, 2)}\n`, "utf8"),
  );
}

/**
 * Reads the bound identity. `undefined` means "unknown" — never "no
 * restriction": every caller must treat it as a reason to REFUSE an
 * approval, not as permission to skip the actor check.
 */
/**
 * True when `actorId` is one of the identities bound to this mission's
 * owner. Both identities name the SAME person (recorded together at init) —
 * this is a namespace join, NOT a loosening of the rule: an actor matching
 * neither is still refused, and a binding that never recorded a Mattermost
 * id refuses every chat-filed actor.
 */
export function isBoundActor(binding: MissionActorBinding, actorId: string): boolean {
  if (actorId.length === 0) return false;
  if (actorId === binding.os_account) return true;
  return binding.mattermost_user_id !== undefined && actorId === binding.mattermost_user_id;
}

export async function readMissionActorBinding(
  controlRoot: string,
): Promise<MissionActorBinding | undefined> {
  let raw: string;
  try {
    raw = await readFile(bindingPath(controlRoot), "utf8");
  } catch {
    return undefined;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<MissionActorBinding>;
    if (typeof parsed.os_account !== "string" || parsed.os_account.length === 0) return undefined;
    if (typeof parsed.bound_at !== "string" || parsed.bound_at.length === 0) return undefined;
    const mattermostUserId =
      typeof parsed.mattermost_user_id === "string" && parsed.mattermost_user_id.length > 0
        ? parsed.mattermost_user_id
        : undefined;
    return {
      os_account: parsed.os_account,
      ...(mattermostUserId ? { mattermost_user_id: mattermostUserId } : {}),
      bound_at: parsed.bound_at,
    };
  } catch {
    return undefined;
  }
}
